import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import restClient from '../../services/rest';
import { getApiErrorMessage } from '../../shared/utils';
import {
  MedicationUploadResult,
  PharmacyPatient,
  PharmacyPatientsActionType,
  PharmacyPatientsApiErrorAction,
  SetPharmacyPatientsAction,
  SetPharmacyPatientsLoadStateAction,
  PharmacyPatientsStore,
  PharmacyUpload,
  SetPharmacyUploadsAction,
  SetPharmacyUploadsLoadStateAction,
} from './pharmacyPatientsTypes';

export const setLoadState = (
  isLoading: boolean,
): SetPharmacyPatientsLoadStateAction => ({
  type: PharmacyPatientsActionType.PHARMACY_PATIENTS_SET_LOAD_STATE,
  payload: { isLoading },
});

export const apiError = (error: any): PharmacyPatientsApiErrorAction => ({
  type: PharmacyPatientsActionType.PHARMACY_PATIENTS_API_ERR,
  payload: { error },
});

export const setPharmacyPatients = (
  patients: PharmacyPatient[],
): SetPharmacyPatientsAction => ({
  type: PharmacyPatientsActionType.SET_PHARMACY_PATIENTS,
  payload: { patients },
});

export const setUploadsLoadState = (
  isUploadsLoading: boolean,
): SetPharmacyUploadsLoadStateAction => ({
  type: PharmacyPatientsActionType.PHARMACY_UPLOADS_SET_LOAD_STATE,
  payload: { isUploadsLoading },
});

export const setPharmacyUploads = (
  uploads: PharmacyUpload[],
): SetPharmacyUploadsAction => ({
  type: PharmacyPatientsActionType.SET_PHARMACY_UPLOADS,
  payload: { uploads },
});

export const fetchPharmacyPatients = () => {
  return async (
    dispatch: ThunkDispatch<PharmacyPatientsStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const response = await restClient.pharmacyTech.getPharmacyTechPatients();
      dispatch(setPharmacyPatients(response ?? []));
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to load patients'));
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const fetchPharmacyUploads = (patientId: number) => {
  return async (
    dispatch: ThunkDispatch<PharmacyPatientsStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setUploadsLoadState(true));
    try {
      const response =
        await restClient.pharmacyTech.getPharmacyTechUploads(patientId);
      dispatch(setPharmacyUploads(response ?? []));
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to load upload history'));
      dispatch(apiError(err));
    } finally {
      dispatch(setUploadsLoadState(false));
    }
  };
};

export const uploadMedicationFile = (
  patientId: number,
  file: File,
  sanitize = false,
) => {
  return async (
    dispatch: ThunkDispatch<PharmacyPatientsStore, void, AnyAction> & Dispatch,
  ): Promise<MedicationUploadResult> => {
    try {
      const response = await restClient.pharmacyTech.uploadPharmacyTechFile(
        patientId,
        { csv_file: file, ...(sanitize && { sanitize: true }) },
      );
      // The server reports fixable CSV issues as a 200 body, not an error
      // status; the caller confirms and retries with sanitize=true.
      if (response?.status === 'issues_found') {
        return { outcome: 'issues_found', issues: response.issues ?? [] };
      }
      toast.success('Medication file uploaded.');
      return { outcome: 'success' };
    } catch (err: any) {
      // Blocking issues (e.g. an empty CSV) come back as a 400 with an
      // issues list; the caller reports them in a dialog, so no toast here.
      if (err?.status === 400 && Array.isArray(err?.body?.issues)) {
        return {
          outcome: 'blocked',
          message: getApiErrorMessage(err, 'This file cannot be uploaded.'),
          issues: err.body.issues,
        };
      }
      toast.error(getApiErrorMessage(err, 'Failed to upload file'));
      dispatch(apiError(err));
      return { outcome: 'error' };
    }
  };
};
